import { useEffect } from 'react';
import { subscribe } from '../utils/dataBus';

/**
 * Re-runs `refetch` when another tab signals hires data may have changed,
 * or when the user switches back to this tab. Mirrors useBudgetInvalidation
 * but listens on the `'hires-changed'` topic.
 *
 *   const fetchData = useCallback(...);
 *   useHiresInvalidation(activeDepartment?.department_id, fetchData);
 *
 * Pass `departmentId` as null/undefined for tabs that show ALL hires
 * (e.g. AdminHires) — the filter then accepts every event.
 */
export default function useHiresInvalidation(departmentId, refetch) {
  useEffect(() => {
    if (typeof refetch !== 'function') return undefined;

    const unsub = subscribe('hires-changed', (payload) => {
      // If the consumer doesn't scope by department (admin view), or the
      // event has no departmentId, accept it. Otherwise only re-fetch
      // when departments match. Coerce to Number for JSON round-trip.
      if (
        departmentId == null ||
        !payload ||
        payload.departmentId == null ||
        Number(payload.departmentId) === Number(departmentId)
      ) {
        refetch();
      }
    });

    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        refetch();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }

    return () => {
      unsub();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [departmentId, refetch]);
}
